import { ApiError } from '@/features/api';
import TwoFactorAuthenticationService, {
  EventType,
  UserSessionTwoFactorStatus,
} from '@/legacy/features/user/twoFactorAuthentication/twoFactorAuthentication';
import EventsService from '@/legacy/services/eventsService';
import { Action } from '@/legacy/utils/reactHelpers';

import { ActionType } from './LoginPopup.reducer';

type Dispatchable<T> = (value: T) => void;

/**
 * Callbacks passed to the hook to enable it to interact with external functionality.
 */
interface Callbacks {
  /**
   * Requests the view to close.
   */
  onClose: () => void;
  /**
   * Requests to proceed with the login successfully.
   */
  onSuccessfulTwoFactorChallenge: () => void;
}

function handleQueries(
  queries: Array<Action<ActionType>>,
  dispatch: Dispatchable<Action<ActionType>>,
  callbacks: Callbacks
): void {
  queries.forEach(async (query) => {
    switch (query.type) {
      case ActionType.QueryBeginAuthentication: {
        try {
          const isAuthenticationStarted = await TwoFactorAuthenticationService.beginAuthentication(
            query.payload
          );

          if (isAuthenticationStarted) {
            dispatch({
              type: ActionType.SetIsAuthenticationStarted,
              payload: isAuthenticationStarted,
            });
          } else {
            dispatch({
              type: ActionType.SetErrorMessage,
              payload: 'Failed initiating authentication. Please try again.',
            });
          }
        } catch (err) {
          if (err.type && err.type === 'rateLimit.smsAuth') {
            dispatch({
              type: ActionType.SetErrorMessage,
              payload: 'Maximum attempts reached. Please try again later.',
            });

            break;
          }

          dispatch({
            type: ActionType.SetErrorMessage,
            payload: 'Failed initiating authentication. Please try again.',
          });
        }
        break;
      }

      case ActionType.QueryBeginCallAuthentication: {
        const errorMessage = 'Failed initializing authentication via phone call. Please try again.';

        try {
          const isAuthenticationStarted =
            await TwoFactorAuthenticationService.beginCallAuthentication(query.payload);

          if (isAuthenticationStarted) {
            dispatch({
              type: ActionType.SetIsAuthenticationStarted,
              payload: isAuthenticationStarted,
            });
          } else {
            dispatch({
              type: ActionType.SetErrorMessage,
              payload: errorMessage,
            });
          }
        } catch (err) {
          dispatch({
            type: ActionType.SetErrorMessage,
            payload: errorMessage,
          });
        }
        break;
      }

      case ActionType.QueryGetTwoFactorAuthentications: {
        try {
          const twoFactorAuthentications =
            await TwoFactorAuthenticationService.getTwoFactorAuthentications();

          dispatch({
            type: ActionType.SetTwoFactorAuthentications,
            payload: twoFactorAuthentications,
          });
        } catch (err) {
          dispatch({
            type: ActionType.SetErrorMessage,
            payload: 'Failed initiating authentication. Please try again.',
          });
        }
        break;
      }

      case ActionType.QuerySubmitCode: {
        const { onClose, onSuccessfulTwoFactorChallenge } = callbacks;
        const { code, id, rememberDevice } = query.payload;

        if (!code) {
          dispatch({
            type: ActionType.SetErrorMessage,
            payload: 'This field is required.',
          });
          return;
        }

        try {
          const authenticationResponse = await TwoFactorAuthenticationService.authenticate(id, {
            code,
            rememberDevice,
          });

          // Authorized is the only expected response in this case
          if (authenticationResponse.status === UserSessionTwoFactorStatus.Authorized) {
            onSuccessfulTwoFactorChallenge();

            EventsService.emit(EventType.UserTwoFactorAuthenticationSuccess);
          } else {
            onClose(); // If we got a different response close the dialog
            dispatch({ type: ActionType.SetIsOpen, payload: false });
          }
        } catch (err) {
          const getErrorMessage = () => {
            const apiError = err as ApiError;

            switch (apiError.type) {
              case 'invalid.deviceId':
                // This can happen when SameSite cookie is not set (or cookies were cleared)
                return '3rd party cookies are disabled. Please check your browser configuration.';
            }

            return apiError.message;
          };

          dispatch({
            type: ActionType.SetErrorMessage,
            payload: getErrorMessage(),
          });
        }
        break;
      }
    }
  });
}

export default handleQueries;
